import { resolveAsset } from '@app-shared/services/asset-resolver/asset-resolver.service';

let clientConfig;

function injectFontFaces(configuration, fontFamily: string) {
  // Remove old style block if re-applied
  const oldStyle = document.getElementById('dynamic-fonts');
  if (oldStyle) oldStyle.remove();

  const style = document.createElement('style');
  style.id = 'dynamic-fonts';
  style.innerHTML = `
    @font-face {
      font-family:'${fontFamily}';
      src: url('${resolveAsset('/fonts/Light.woff2')}') format('woff2'),
           url('${resolveAsset('/fonts/Light.woff')}') format('woff');
      font-weight: ${configuration.lightFontWeight};
      font-style: normal;
      font-display: swap;
      text-rendering: optimizeLegibility;
    }
    @font-face {
      font-family: '${fontFamily}';
      src: url('${resolveAsset('/fonts/Regular.woff2')}') format('woff2'),
           url('${resolveAsset('/fonts/Regular.woff')}') format('woff');
      font-weight: ${configuration.normalFontWeight};
      font-style: normal;
      font-display: swap;
      text-rendering: optimizeLegibility;
    }
    @font-face {
      font-family: '${fontFamily}';
      src: url('${resolveAsset('/fonts/Bold.woff2')}') format('woff2'),
           url('${resolveAsset('/fonts/Bold.woff')}') format('woff');
      font-weight: ${configuration.boldFontWeight};
      font-style: normal;
      font-display: swap;
      text-rendering: optimizeLegibility;
    }
  `;
  document.head.appendChild(style);
}

function injectCustomCss(customCssPath: string) {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = resolveAsset(customCssPath);
  link.type = 'text/css';
  document.head.appendChild(link);
}

export function applyManifest(config: any): void {
  const favicon = config.configuration?.favicon;
  if (!favicon) return;

  const manifestHref: string = 'manifest.json';

  // Remove existing manifest if any
  const existing = document.querySelector("link[rel='manifest']");
  if (existing) {
    existing.remove();
  }

  // Create new manifest link
  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = manifestHref;

  document.head.appendChild(link);
}

// Called from main.ts after fetching config.json
export function setClientConfig(config) {
  clientConfig = config;
}

// Called anywhere in app
export function getClientConfig() {
  return clientConfig;
}

function injectClientIndexFont(fontFamily: string) {
  const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500&display=swap`;

  const link = document.createElement('link');
  link.id = 'index-font';
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
}

export function setClientIndex(config) {
  if (config.title) {
    document.title = config.title;
  }

  // Check if running in iframe
  const isIframe = window.self !== window.top;

  // Handle favicon based on config availability
  if (config?.configuration?.favicon && !config.environments?.isGeotabEnv) {
    // Config has favicon - set it
    let favicon = document.querySelector("link[rel='icon']");

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }

    favicon.setAttribute('href', resolveAsset(config.configuration.favicon));
  } else {
    // Config is missing or has no favicon - remove existing favicon
    // Only remove if not in iframe to avoid affecting parent document
    const existingFavicon = document.querySelector("link[rel='icon']");
    if (existingFavicon && !isIframe) {
      existingFavicon.remove();
    } else if (existingFavicon && isIframe) {
      // In iframe context, set to empty data URL to prevent fallback to parent
      existingFavicon.setAttribute('href', 'data:,');
    }
  }

  if (config.themeColor) {
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeMeta);
    }
    themeMeta.setAttribute('content', config.themeColor);
  }

  if (config?.configuration?.useScripts) {
    const script = document.createElement('script');
    script.src = resolveAsset('/scripts.js');
    script.async = true;
    document.head.appendChild(script);
  }
}

export function applyClientColorsFonts(config) {
  const cssVars = {
    '--primary-color': config.configuration.lightPrimary,
    '--primary-contrast': config.configuration.lightPrimaryContrast,
    '--dark-primary-color': config.configuration.darkPrimary,
    '--dark-primary-contrast': config.configuration.darkPrimaryContrast,
    '--accent-color': config.configuration.lightAccent,
    '--accent-contrast': config.configuration.lightAccentContrast,
    '--dark-accent-color': config.configuration.darkAccent,
    '--dark-accent-contrast': config.configuration.darkAccentContrast,
    '--page-background': config.configuration.pageBackground,
    '--large-font-size': config.configuration.largeFontSize,
    '--heading-font-size': config.configuration.headingFontSize,
    '--body-font-size': config.configuration.bodyFontSize,
    '--caption-font-size': config.configuration.captionFontSize,
    '--light-font-weight': config.configuration.lightFontWeight,
    '--normal-font-weight': config.configuration.normalFontWeight,
    '--medium-font-weight': config.configuration.mediumFontWeight,
    '--bold-font-weight': config.configuration.boldFontWeight,
    '--font-family': `${config.fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue',sans-serif`,
  };

  for (const [key, value] of Object.entries(cssVars)) {
    if (value) document.documentElement.style.setProperty(key, value as string);
  }

  // ✅ Inject @font-face dynamically if custom fonts enabled
  if (config.configuration?.useCustomFonts && config.fontFamily) {
    injectFontFaces(config.configuration, config.fontFamily);
  } else if (config?.fontFamily) {
    // ✅ Load index font from Google Fonts only if NOT using custom fonts
    injectClientIndexFont(config.fontFamily);
  }

  // ✅ Conditionally load custom CSS
  if (config.configuration?.useCustomCss && config.configuration?.['custom-styles']) {
    injectCustomCss(config.configuration?.['custom-styles']);
  }
}

export const extractConfig = (response, isSSO: boolean) => {
  if (isSSO && response.sso) {
    const result = { ...response };
    result.configuration = response.sso.ssoConfigurations;
    result.environments = response.sso.ssoEnvironment;
    delete result.sso;
    delete result.cognito;
    return result;
  } else {
    const result = { ...response };
    result.configuration = response.cognito.cognitoConfigurations;
    result.environments = response.cognito.cognitoEnvironment;
    delete result.cognito;
    delete result.sso;
    return result;
  }
};
